import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X,
  Package,
  MapPin,
  FileText,
  CheckCircle,
  AlertCircle,
  Home,
  Clock,
  Sun,
  Sunset,
} from 'lucide-react';
import DateSelector from './DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

// Districts restricted to afternoon slot only (11am cutoff for today)
const AFTERNOON_ONLY_DISTRICTS = [
  'Carabayllo',
  'Ventanilla',
  'Puente Piedra',
  'Villa El Salvador',
  'Villa María del Triunfo',
  'Chorrillos',
  'Ate',
  'San Juan de Lurigancho',
];

function AddRecojoModal({ userId, user, onClose, onSuccess }) {
  const [pickupDate, setPickupDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [pickupTimeSlot, setPickupTimeSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [driverId, setDriverId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch user addresses on mount
  useEffect(() => {
    fetchUserAddresses();
  }, []);

  // Reset time slot when district changes (far districts can't use morning)
  useEffect(() => {
    const selectedAddr = addresses.find(a => a.id === parseInt(selectedAddressId));
    if (selectedAddr && isFarDistrict(selectedAddr.district)) {
      setPickupTimeSlot('afternoon');
    } else {
      setPickupTimeSlot('');
    }
  }, [selectedAddressId, addresses]);

  const fetchUserAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.addresses && response.data.addresses.length > 0) {
        setAddresses(response.data.addresses);
        setSelectedAddressId(response.data.addresses[0].id);
        assignDriverByDistrict(response.data.addresses[0].district);
      }
    } catch (error) {
      console.error('Error fetching user addresses:', error);
    }
  };

  const isFarDistrict = (district) => {
    if (!district) return false;
    return AFTERNOON_ONLY_DISTRICTS.some(
      d => d.toLowerCase() === district.toLowerCase()
    );
  };

  // ── Time slot availability helpers (mirrors backend logic) ────────────────
  // Returns the current Peru time info needed for cutoff checks
  const getPeruTimeInfo = () => {
    // Peru is UTC-5, no DST
    const now = new Date();
    const peruOffset = -5 * 60;
    const localOffset = now.getTimezoneOffset();
    const peruTime = new Date(now.getTime() + (localOffset - peruOffset) * 60000);
    return {
      hour: peruTime.getHours(),
      date: peruTime.toISOString().split('T')[0],
    };
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    return dateStr === getPeruTimeInfo().date;
  };

  const isMorningAvailable = (district) => {
    // Far districts can never use morning
    if (isFarDistrict(district)) return false;
    // For future dates, always available
    if (!isToday(pickupDate)) return true;
    // For today, check 7am cutoff
    return getPeruTimeInfo().hour < 7;
  };

  const isAfternoonAvailable = (district) => {
    // For future dates, always available
    if (!isToday(pickupDate)) return true;
    // For today, cutoff varies by district
    const { hour } = getPeruTimeInfo();
    return isFarDistrict(district) ? hour < 11 : hour < 13;
  };

  const getMorningUnavailableReason = (district) => {
    if (isFarDistrict(district)) {
      return `El distrito ${district} solo permite horario de tarde por distancia`;
    }
    if (isToday(pickupDate) && getPeruTimeInfo().hour >= 7) {
      return 'Pasó el límite de las 7:00 AM para hoy';
    }
    return null;
  };

  const getAfternoonUnavailableReason = (district) => {
    if (!isToday(pickupDate)) return null;
    const { hour } = getPeruTimeInfo();
    if (isFarDistrict(district) && hour >= 11) {
      return `Pasó el límite de las 11:00 AM para ${district} hoy`;
    }
    if (!isFarDistrict(district) && hour >= 13) {
      return 'Pasó el límite de la 1:00 PM para hoy';
    }
    return null;
  };

  const assignDriverByDistrict = (district) => {
    const driver_1  = ["Chorrillos", "San Juan de Miraflores", "Villa El Salvador", "Villa Maria del Triunfo"];
    const driver_2  = ["Comas", "Independencia", "San Martín de Porres", "Los Olivos", "Rímac"];
    const driver_3  = ["Ate", "Santa Anita", "La Molina", "San Luis"];
    const driver_4  = ["Callao", "Jesús María", "Bellavista", "Lince", "Magdalena del Mar", "Carmen de la Legua", "La Perla", "La Punta", "Cercado de Lima", "San Miguel", "Pueblo Libre", "Breña"];
    const driver_5  = ["Puente Piedra", "Carabayllo", "Ventanilla", "Ancon", "Santa Rosa"];
    const driver_6  = ["San Isidro", "San Borja", "Miraflores", "Surquillo", "Santiago de Surco", "Barranco"];
    const driver_7  = ["El Agustino", "La Victoria", "San Juan de Lurigancho"];
    const driver_10 = ["Ayacucho"];
    const driver_11 = ["Arequipa"];
    const driver_12 = ["Juliaca"];
    const driver_13 = ["Puno"];
    const driver_14 = ["Chiclayo"];
    const driver_15 = ["Cusco"];
    const driver_16 = ["Huancayo"];
    const driver_17 = ["Chimbote"];
    const driver_18 = ["Piura"];
    const driver_19 = ["Trujillo"];

    if (driver_1.includes(district))       setDriverId(7);
    else if (driver_2.includes(district))  setDriverId(3);
    else if (driver_3.includes(district))  setDriverId(6);
    else if (driver_4.includes(district))  setDriverId(54);
    else if (driver_5.includes(district))  setDriverId(183);
    else if (driver_6.includes(district))  setDriverId(164);
    else if (driver_7.includes(district))  setDriverId(119);
    else if (driver_10.includes(district)) setDriverId(450);
    else if (driver_11.includes(district)) setDriverId(451);
    else if (driver_12.includes(district)) setDriverId(452);
    else if (driver_13.includes(district)) setDriverId(453);
    else if (driver_14.includes(district)) setDriverId(454);
    else if (driver_15.includes(district)) setDriverId(455);
    else if (driver_16.includes(district)) setDriverId(456);
    else if (driver_17.includes(district)) setDriverId(458);
    else if (driver_18.includes(district)) setDriverId(459);
    else if (driver_19.includes(district)) setDriverId(460);
    else setDriverId(null);
  };

  const handleAddressChange = (addressId) => {
    setSelectedAddressId(addressId);
    const selectedAddr = addresses.find(a => a.id === parseInt(addressId));
    if (selectedAddr?.district) {
      assignDriverByDistrict(selectedAddr.district);
    }
  };

  const validateForm = () => {
    if (!selectedAddressId) {
      setErrorMessage('Por favor, selecciona una dirección de recojo.');
      return false;
    }
    if (!pickupDate) {
      setErrorMessage('Por favor, selecciona la fecha de recojo.');
      return false;
    }
    if (!pickupTimeSlot) {
      setErrorMessage('Por favor, selecciona el horario de recojo (mañana o tarde).');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBusy(true);
    setErrorMessage('');
    try {
      await axios.post(`${API_URL}/api/recojo`, {
        user_id: userId,
        address_id: selectedAddressId,
        driver_id: driverId,
        pickup_date: pickupDate,
        pickup_time_slot: pickupTimeSlot,
        notes,
      });
      setShowSuccessModal(true);
      setTimeout(() => onSuccess(), 1500);
    } catch (error) {
      console.error('Error creating recojo:', error);
      setErrorMessage(
        error.response?.data?.message || 'Error al crear el recojo. Por favor, intenta de nuevo.'
      );
    } finally {
      setBusy(false);
    }
  };

  // ── Derive district-aware slot availability ────────────────────────────────
  const selectedAddr = addresses.find(a => a.id === parseInt(selectedAddressId));
  const district = selectedAddr?.district || '';
  const farDistrict = isFarDistrict(district);
  const morningAvailable = isMorningAvailable(district);
  const afternoonAvailable = isAfternoonAvailable(district);
  const morningReason = getMorningUnavailableReason(district);
  const afternoonReason = getAfternoonUnavailableReason(district);

  // ── Slot card component ───────────────────────────────────────────────────
  const SlotCard = ({ slot, label, timeRange, deliveryRange, icon: Icon, available, unavailableReason, accentColor }) => {
    const isSelected = pickupTimeSlot === slot;
    return (
      <button
        type="button"
        disabled={!available}
        onClick={() => available && setPickupTimeSlot(slot)}
        className={`
          w-full text-left rounded-xl border-2 p-4 transition-all
          ${isSelected
            ? `border-[${accentColor}] bg-[${accentColor}]/5 shadow-sm`
            : available
              ? 'border-slate-200 hover:border-slate-300 bg-white'
              : 'border-slate-100 bg-slate-50 cursor-not-allowed opacity-60'
          }
        `}
        style={isSelected ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}
      >
        <div className="flex items-start gap-3">
          {/* Icon bubble */}
          <div
            className="p-2 rounded-lg flex-shrink-0 mt-0.5"
            style={{ backgroundColor: isSelected ? `${accentColor}20` : '#f1f5f9' }}
          >
            <Icon
              className="w-5 h-5"
              style={{ color: isSelected ? accentColor : '#94a3b8' }}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p
                className="text-sm font-bold font-montserrat"
                style={{ color: isSelected ? accentColor : available ? '#1e293b' : '#94a3b8' }}
              >
                {label}
              </p>
              {isSelected && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  Seleccionado
                </span>
              )}
              {!available && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">
                  No disponible
                </span>
              )}
            </div>

            <div className="space-y-0.5">
              <p className="text-xs text-slate-600 font-montserrat">
                <span className="font-medium">Recojo:</span> {timeRange}
              </p>
              <p className="text-xs text-slate-500 font-montserrat">
                <span className="font-medium">Entrega:</span> {deliveryRange}
              </p>
            </div>

            {/* Unavailability reason */}
            {!available && unavailableReason && (
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 font-montserrat">
                ⚠ {unavailableReason}
              </p>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-[#E6B43C]/5 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#E6B43C]/10 rounded-lg">
                  <Package className="w-6 h-6 text-[#E6B43C]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 font-montserrat">Nuevo Recojo</h3>
                  <p className="text-sm text-slate-500 font-montserrat">
                    Para: <span className="font-medium text-slate-700">{user.username}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={busy}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">

              {/* No addresses warning */}
              {addresses.length === 0 && (
                <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 font-montserrat">
                      Este usuario no tiene direcciones registradas
                    </p>
                    <p className="text-xs text-amber-700 mt-1 font-montserrat">
                      El usuario debe agregar al menos una dirección antes de poder crear un recojo.
                    </p>
                  </div>
                </div>
              )}

              {/* Fecha */}
              <DateSelector
                selectedDate={pickupDate}
                onDateChange={setPickupDate}
                showCalendar={showCalendar}
                onToggleCalendar={() => setShowCalendar(!showCalendar)}
              />

              {/* Dirección de Recojo */}
              <div>
                <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                  <Home className="h-4 w-4 text-[#B1336E]" />
                  Dirección de Recojo *
                </label>
                {addresses.length > 0 ? (
                  <select
                    value={selectedAddressId}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#B1336E] transition-all font-montserrat"
                  >
                    {addresses.map((addr) => (
                      <option key={addr.id} value={addr.id}>
                        {addr.label || `Dirección ${addr.id}`} - {addr.address}
                        {addr.district ? ` (${addr.district})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-400 font-montserrat">
                    No hay direcciones disponibles
                  </div>
                )}
              </div>

              {/* Selected Address Details */}
              {selectedAddressId && addresses.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#E6B43C] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800 font-montserrat">
                        Dirección seleccionada
                      </p>
                      {(() => {
                        const addr = addresses.find(a => a.id === parseInt(selectedAddressId));
                        return addr ? (
                          <div className="mt-1 text-sm text-slate-600 space-y-1">
                            <p className="font-montserrat">{addr.address}</p>
                            {addr.district && (
                              <p className="text-xs font-montserrat">
                                Distrito: <span className="font-medium">{addr.district}</span>
                                {farDistrict && (
                                  <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-montserrat">
                                    Distrito lejano · solo tarde
                                  </span>
                                )}
                              </p>
                            )}
                            {addr.reference && (
                              <p className="text-xs text-slate-500 font-montserrat">
                                Referencia: {addr.reference}
                              </p>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Time Slot Selector ─────────────────────────────────────── */}
              <div>
                <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800 font-montserrat">
                  <Clock className="h-4 w-4 text-[#B1336E]" />
                  Horario de Recojo *
                  {!pickupDate && (
                    <span className="text-xs font-normal text-slate-400">(selecciona fecha primero)</span>
                  )}
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SlotCard
                    slot="morning"
                    label="Mañana"
                    timeRange="6:00 AM – 9:00 AM"
                    deliveryRange="10:00 AM – 8:00 PM"
                    icon={Sun}
                    available={!!pickupDate && morningAvailable}
                    unavailableReason={pickupDate ? morningReason : null}
                    accentColor="#E6B43C"
                  />
                  <SlotCard
                    slot="afternoon"
                    label="Tarde"
                    timeRange="10:00 AM – 1:00 PM"
                    deliveryRange="2:00 PM – 8:00 PM"
                    icon={Sunset}
                    available={!!pickupDate && afternoonAvailable}
                    unavailableReason={pickupDate ? afternoonReason : null}
                    accentColor="#B1336E"
                  />
                </div>

                {/* Both slots unavailable for today */}
                {pickupDate && isToday(pickupDate) && !morningAvailable && !afternoonAvailable && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700 font-montserrat">
                      No hay horarios disponibles para hoy. Por favor selecciona una fecha futura.
                    </p>
                  </div>
                )}
              </div>

              {/* Notas */}
              <div>
                <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                  <FileText className="h-4 w-4 text-[#67A43C]" />
                  Notas adicionales
                  <span className="text-xs font-normal text-slate-500">(opcional)</span>
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Información adicional para el recojo (ej: piso, departamento, horario preferido)..."
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#67A43C] transition-all resize-none font-montserrat"
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-slate-400 text-right font-montserrat">
                  {notes.length}/500
                </p>
              </div>

              {/* Driver Assignment Info */}
              {driverId && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-800 font-montserrat">
                        Conductor asignado automáticamente
                      </p>
                      <p className="text-xs text-blue-700 mt-1 font-montserrat">
                        Se asignó el conductor ID #{driverId} según el distrito de la dirección.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-montserrat">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-white transition-all text-sm font-medium text-slate-700 disabled:opacity-50 font-montserrat"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={busy || addresses.length === 0}
              className="px-5 py-2.5 rounded-xl text-white transition-all text-sm font-medium disabled:opacity-50 shadow-lg font-montserrat"
              style={{ backgroundColor: '#E6B43C' }}
            >
              {busy ? 'Creando...' : 'Crear Recojo'}
            </button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-green-600 mb-2 font-montserrat">
                ¡Recojo Creado!
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                El recojo se ha registrado exitosamente para {user.username}.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AddRecojoModal;